/* eslint-disable react/no-unescaped-entities */
import React, { useState } from "react";
import { AlertCircle, Sparkles } from "lucide-react";
import { SEO } from "@/components/SEO";
import { Section, SpecLabel } from "@/components/Section";
import { BookACallButton } from "@/components/BookACallButton";
import { SystemIcon } from "@/components/SystemIcon";
import { SYSTEMS } from "@/data/systems";
import { cn } from "@/lib/utils";

export default function PricingPage() {
    const [billing, setBilling] = useState("annual");
    const monthly = 297;
    const annual = monthly * 12;

    return (
        <>
            <SEO
                title="Pricing — $297/Month, All 7 Marketing Systems for Home Service Pros"
                description="Flat $297/month. All seven Elevara Systems included — website, reviews, missed-call text-back, campaigns, local SEO, unified inbox, business phone. Annual plan includes free Google Business Profile optimization ($450 value). No contracts. Cancel anytime."
                path="/pricing"
            />

            {/* HERO */}
            <section
                className="relative overflow-hidden bg-white"
                data-testid="pricing-hero"
            >
                <div
                    className="absolute inset-0 blueprint-grid opacity-40 pointer-events-none"
                    aria-hidden="true"
                />
                <div
                    className="absolute inset-0 hero-edge-glow pointer-events-none"
                    aria-hidden="true"
                />
                <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                        <div className="lg:col-span-7">
                            <SpecLabel>Pricing — transparent on purpose</SpecLabel>
                            <h1 className="mt-3 sm:mt-4 font-display font-extrabold tracking-tight text-[#0a0b0d] text-4xl sm:text-5xl lg:text-[60px] leading-[1.04]">
                                One plan. One price.
                                <br />
                                <span className="text-[#188bf6]">All seven systems.</span>
                            </h1>
                            <p className="mt-5 text-sm sm:text-base lg:text-lg text-[#4a5562] max-w-xl leading-relaxed">
                                No setup fee. No contracts. No “call for
                                pricing.” $297/mo gets you the whole stack.
                                Cancel anytime, and we mean it.
                            </p>
                        </div>
                        <div className="lg:col-span-5 relative hidden lg:block">
                            <div
                                className="absolute -inset-12 rounded-full blur-3xl"
                                style={{
                                    background:
                                        "radial-gradient(closest-side, rgba(24,139,246,0.22), transparent 70%)",
                                }}
                                aria-hidden="true"
                            />
                            <img
                                src="/assets/mascot_pose3.png"
                                alt=""
                                className="relative w-full max-w-[460px] mx-auto h-auto select-none drop-shadow-[0_28px_38px_rgba(10,11,13,0.18)]"
                                loading="eager"
                                aria-hidden="true"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* PRICING CARD */}
            <section
                className="bg-[#F7F8FA] py-12 sm:py-16 lg:py-20"
                data-testid="pricing-card-section"
            >
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="rounded-3xl border-2 border-[#0a0b0d]/14 bg-white shadow-[0_30px_80px_-40px_rgba(10,11,13,0.25)] overflow-hidden card-lift">
                        <div className="h-1.5 bg-[#188bf6]" aria-hidden="true" />
                        <div className="p-5 sm:p-8 lg:p-12">
                            {/* Header row */}
                            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                                <div>
                                    <div className="text-[11px] font-display font-bold uppercase tracking-[0.2em] text-[#4a5562]">
                                        Foundational Advanced
                                    </div>
                                    <h2 className="mt-2 font-display font-extrabold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-[#0a0b0d]">
                                        The plan. The only plan.
                                    </h2>
                                </div>
                                {/* Toggle */}
                                <div
                                    className="inline-flex p-1 rounded-xl border-2 border-[#0a0b0d]/14 bg-[#F7F8FA] self-start sm:self-auto"
                                    role="tablist"
                                    data-testid="pricing-billing-toggle"
                                >
                                    <button
                                        type="button"
                                        role="tab"
                                        aria-selected={billing === "monthly"}
                                        data-testid="pricing-monthly-option"
                                        onClick={() => setBilling("monthly")}
                                        className={cn(
                                            "px-4 sm:px-5 h-9 sm:h-10 rounded-lg text-[13px] sm:text-sm font-display font-bold transition-colors",
                                            billing === "monthly"
                                                ? "bg-[#0a0b0d] text-white"
                                                : "text-[#0a0b0d]/65 hover:text-[#0a0b0d]",
                                        )}
                                    >
                                        Monthly
                                    </button>
                                    <button
                                        type="button"
                                        role="tab"
                                        aria-selected={billing === "annual"}
                                        data-testid="pricing-annual-option"
                                        onClick={() => setBilling("annual")}
                                        className={cn(
                                            "px-4 sm:px-5 h-9 sm:h-10 rounded-lg text-[13px] sm:text-sm font-display font-bold transition-colors",
                                            billing === "annual"
                                                ? "bg-[#188bf6] text-white"
                                                : "text-[#0a0b0d]/65 hover:text-[#0a0b0d]",
                                        )}
                                    >
                                        Annual
                                    </button>
                                </div>
                            </div>

                            {/* Price block — with INLINE annual notice */}
                            <div className="mt-6 sm:mt-8">
                                <div className="flex items-baseline gap-3 flex-wrap">
                                    <span className="font-display text-5xl sm:text-7xl lg:text-8xl font-extrabold tracking-tight text-[#0a0b0d]">
                                        ${monthly}
                                    </span>
                                    <span className="text-[#4a5562] text-sm sm:text-base font-display font-medium">
                                        {billing === "monthly"
                                            ? "/ month, billed monthly"
                                            : `/ month, billed annually ($${annual.toLocaleString()})`}
                                    </span>
                                </div>
                                {/* Inline annual incentive — small, blue, sits with the price */}
                                {billing === "annual" ? (
                                    <div
                                        data-testid="pricing-annual-inline-notice"
                                        className="mt-3 inline-flex items-center gap-2 text-[12px] sm:text-sm text-[#0b72d8] font-display font-bold"
                                    >
                                        <Sparkles className="w-4 h-4" aria-hidden="true" />
                                        <span>
                                            + Free Google My Business
                                            Optimization ($450 value)
                                        </span>
                                    </div>
                                ) : null}
                            </div>

                            {/* Includes list — with icons */}
                            <div className="mt-8 sm:mt-10">
                                <div className="text-[11px] font-display font-bold uppercase tracking-[0.2em] text-[#4a5562]">
                                    Includes — every system, every account
                                </div>
                                <ul className="mt-4 sm:mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3">
                                    {SYSTEMS.map((s) => (
                                        <li
                                            key={s.slug}
                                            className="flex items-center gap-3 p-2.5 sm:p-3 rounded-xl border border-[#0a0b0d]/14 bg-white hover:border-[#188bf6]/50 transition-colors"
                                        >
                                            <span className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-[#188bf6]/10 border border-[#188bf6]/22 flex items-center justify-center shrink-0">
                                                <SystemIcon
                                                    slug={s.slug}
                                                    className="w-4 h-4 text-[#188bf6]"
                                                />
                                            </span>
                                            <span className="font-display font-bold text-[13px] sm:text-[14px] text-[#0a0b0d]">
                                                {s.name}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* CTAs */}
                            <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
                                <BookACallButton
                                    size="lg"
                                    testId="pricing-book-a-call-button"
                                />
                                <p className="text-xs sm:text-sm text-[#4a5562]">
                                    No setup fee. No contracts. Cancel
                                    anytime.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Trust strip */}
                    <div className="mt-5 sm:mt-6 grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-4">
                        {[
                            { l: "Setup", v: "$0" },
                            { l: "Contract", v: "None" },
                            { l: "Annual bonus", v: "$450 GMB" },
                            { l: "Cancel", v: "Anytime" },
                        ].map((it) => (
                            <div
                                key={it.l}
                                className="rounded-xl border-2 border-[#0a0b0d]/14 bg-white p-3 sm:p-4 card-lift"
                            >
                                <div className="text-[9px] sm:text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#4a5562]">
                                    {it.l}
                                </div>
                                <div className="mt-1 font-display font-extrabold text-base sm:text-xl text-[#0a0b0d]">
                                    {it.v}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* UPSELLS */}
            <Section testId="pricing-upsells">
                <div className="max-w-3xl">
                    <SpecLabel>Upsells (for active subscribers only)</SpecLabel>
                    <h2 className="mt-3 sm:mt-4 font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-[#0a0b0d]">
                        Add-ons that only work when the foundation is in place.
                    </h2>
                    <p className="mt-4 sm:mt-5 text-sm sm:text-base text-[#4a5562]">
                        These services are available only to active
                        Foundational Advanced subscribers. They aren't sold
                        standalone, and we're upfront about why — without
                        the foundation, they don't perform.
                    </p>
                </div>
                <div className="mt-8 sm:mt-10 grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                    {[
                        {
                            title: "Advanced SEO",
                            desc: "Super-targeted local SEO beyond what's included in the foundation — deeper keyword targeting, content programs, and ongoing competitor work.",
                        },
                        {
                            title: "Google Ads (Fully Managed)",
                            desc: "Fully managed Google Ads campaigns built on top of your foundation. We don't run paid traffic into a site that can't convert — that's how money gets lit on fire.",
                        },
                    ].map((u) => (
                        <div
                            key={u.title}
                            data-testid={`upsell-card-${u.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            className="rounded-2xl border-2 border-[#0a0b0d]/14 bg-white p-5 sm:p-7 hover:border-[#188bf6]/50 transition-colors card-lift"
                        >
                            <div className="flex items-center justify-between gap-3">
                                <h3 className="font-display text-lg sm:text-xl font-extrabold text-[#0a0b0d]">
                                    {u.title}
                                </h3>
                                <span className="text-[9px] sm:text-[10px] font-display font-bold uppercase tracking-[0.18em] text-[#188bf6] border-2 border-[#188bf6]/40 rounded-full px-2.5 py-1 whitespace-nowrap">
                                    Subscribers only
                                </span>
                            </div>
                            <p className="mt-3 text-sm text-[#4a5562] leading-relaxed">
                                {u.desc}
                            </p>
                        </div>
                    ))}
                </div>
                <div className="mt-8 sm:mt-10 rounded-2xl border-2 border-[#0a0b0d]/14 bg-[#F7F8FA] p-4 sm:p-6 flex items-start gap-3">
                    <AlertCircle
                        className="w-5 h-5 text-[#188bf6] mt-0.5 shrink-0"
                        aria-hidden="true"
                    />
                    <p className="text-xs sm:text-sm text-[#0a0b0d] font-display font-medium leading-relaxed">
                        Upsells are not available as standalone services.
                        The foundation must be in place first — without it,
                        these services won't perform. We're upfront about
                        that.
                    </p>
                </div>
            </Section>

            {/* FINAL CTA */}
            <section
                className="relative bg-[#0a0b0d] text-white overflow-hidden"
                data-testid="pricing-final-cta"
            >
                <div className="h-1 caution-stripe" aria-hidden="true" />
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                        <div className="lg:col-span-8">
                            <h2 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight">
                                Same number every month.
                                <br />
                                <span className="text-[#5BA9F8]">No bull*.</span>
                            </h2>
                            <div className="mt-6 sm:mt-8">
                                <BookACallButton
                                    size="lg"
                                    testId="pricing-final-cta-button"
                                />
                            </div>
                        </div>
                        <div className="lg:col-span-4 hidden lg:block">
                            <img
                                src="/assets/mascot_pose5.png"
                                alt=""
                                className="w-full max-w-[260px] mx-auto h-auto"
                                loading="lazy"
                                aria-hidden="true"
                            />
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
